/* eslint-disable no-restricted-syntax -- The admin's own design system (shared/rd), ported from the Restaurant Dashboard design; admin chose app-local components over @a2z/ui-v2 (2026-10-26). See apps/admin/AGENTS.md. */
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  ChevronDown,
  ChevronsUpDown,
  Languages,
  LogOut,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Sun,
} from "lucide-react";
import { Badge, Icon } from "./icons";
import { cx } from "./cx";
import { Button, SearchInput, SegmentedControl, type SegmentOption } from "./controls";

export type SidebarItem = {
  key: string;
  label: string;
  href: string;
  icon: LucideIcon;
  active: boolean;
  count?: number;
};

export type SidebarGroup = {
  key: string;
  label: string;
  /** Doodle id for the section parent (24px). */
  badge: string;
  items: SidebarItem[];
};

export type SidebarLabels = {
  open: string;
  close: string;
  home: string;
  search: string;
  account: string;
  language: string;
  theme: string;
  light: string;
  dark: string;
  signOut: string;
  noMatches: string;
};

type SidebarProps = {
  brand: string;
  mark: string;
  markDark: string;
  groups: SidebarGroup[];
  labels: SidebarLabels;
  accountName: string;
  accountRole?: string | null;
  accountEmail?: string | null;
  language: string;
  languages: Array<SegmentOption>;
  onLanguage: (value: string) => void;
  theme: "light" | "dark";
  onTheme: (value: "light" | "dark") => void;
  accountItems?: Array<{ key: string; label: string; href: string; icon: LucideIcon }>;
  onSignOut: () => void;
  /** Folded to the 52px rail. */
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Narrow screens: float over a scrim, close after navigation. */
  floating?: boolean;
  onClose?: () => void;
};

function initialsOf(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/**
 * The 240px navigation: brand row, search, collapsible sections (a Doodle
 * badge per section, Lucide icons per page) and the account card, whose menu
 * holds language, theme and sign out.
 */
export function Sidebar(props: SidebarProps) {
  const { labels } = props;
  const [closedGroups, setClosedGroups] = useState<Record<string, boolean>>({});
  const [query, setQuery] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  if (props.collapsed && !props.floating) {
    return (
      <nav className="rd-side rd-side-collapsed" aria-label={labels.home}>
        <Button variant="ghost" iconOnly icon={PanelLeftOpen} title={labels.open} onClick={props.onToggleCollapsed} className="rd-flip-rtl" />
      </nav>
    );
  }

  const needle = query.trim().toLocaleLowerCase();
  const groups = props.groups
    .map((group) => ({
      ...group,
      items: needle ? group.items.filter((item) => item.label.toLocaleLowerCase().includes(needle)) : group.items,
    }))
    .filter((group) => group.items.length > 0);

  return (
    <nav className={cx("rd-side", props.floating && "rd-side-floating")} aria-label={labels.home}>
      <div className="rd-side-brand">
        <span className="rd-side-home">
          <span className="rd-side-markbox" aria-hidden="true">
            <img className="rd-side-mark rd-logo-light" src={props.mark} alt="" />
            <img className="rd-side-mark rd-logo-dark" src={props.markDark} alt="" />
          </span>
          <span className="rd-side-name">
            <b>{props.brand}</b>
          </span>
        </span>
        <Button
          variant="ghost"
          iconOnly
          icon={PanelLeftClose}
          title={labels.close}
          className="rd-flip-rtl"
          onClick={props.floating ? props.onClose : props.onToggleCollapsed}
        />
      </div>

      <SearchInput placeholder={labels.search} aria-label={labels.search} value={query} onChange={(event) => setQuery(event.target.value)} />

      {groups.length === 0 ? <p className="rd-side-foot" style={{ marginTop: 8 }}>{labels.noMatches}</p> : null}

      {groups.map((group) => {
        const open = needle ? true : !closedGroups[group.key];
        return (
          <div key={group.key} className="rd-side-group">
            <button
              type="button"
              className="rd-nav-parent"
              aria-expanded={open}
              onClick={() => setClosedGroups((current) => ({ ...current, [group.key]: open }))}
            >
              <Badge id={group.badge} size={24} />
              <span style={{ flex: 1 }}>{group.label}</span>
              <span className="rd-nav-chev">
                <Icon icon={ChevronDown} size={14} />
              </span>
            </button>
            {open
              ? group.items.map((item) => (
                  <Link
                    key={item.key}
                    to={item.href}
                    className="rd-nav-item"
                    aria-current={item.active ? "page" : undefined}
                    onClick={props.floating ? props.onClose : undefined}
                  >
                    <span style={{ width: 18, height: 18, flex: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Icon icon={item.icon} size={18} color="var(--rd-text-icon)" stroke={1.8} />
                    </span>
                    <span style={{ flex: 1 }}>{item.label}</span>
                    {item.count ? <span className="rd-nav-count">{item.count}</span> : null}
                  </Link>
                ))
              : null}
          </div>
        );
      })}

      <div className="rd-side-bottom">
        {menuOpen ? (
          <>
            <div className="rd-acct-scrim" onClick={() => setMenuOpen(false)} />
            <div className="rd-acct-menu" role="menu" aria-label={labels.account}>
              <div className="rd-acct-head">
                <b dir="auto">{props.accountName}</b>
                {props.accountEmail || props.accountRole ? <span dir="auto">{props.accountEmail || props.accountRole}</span> : null}
              </div>
              <div className="rd-acct-sep" />
              <div className="rd-acct-row">
                <span className="rd-acct-label">
                  <Icon icon={Languages} size={16} color="var(--rd-text-icon)" />
                  {labels.language}
                </span>
                <SegmentedControl label={labels.language} options={props.languages} value={props.language} onChange={props.onLanguage} />
              </div>
              <div className="rd-acct-row">
                <span className="rd-acct-label">
                  <Icon icon={props.theme === "dark" ? Moon : Sun} size={16} color="var(--rd-text-icon)" />
                  {labels.theme}
                </span>
                <SegmentedControl
                  label={labels.theme}
                  options={[
                    { value: "light", label: labels.light },
                    { value: "dark", label: labels.dark },
                  ]}
                  value={props.theme}
                  onChange={(value) => props.onTheme(value as "light" | "dark")}
                />
              </div>
              <div className="rd-acct-sep" />
              {(props.accountItems ?? []).map((item) => (
                <Link
                  key={item.key}
                  to={item.href}
                  role="menuitem"
                  className="rd-acct-item"
                  onClick={() => {
                    setMenuOpen(false);
                    props.onClose?.();
                  }}
                >
                  <Icon icon={item.icon} size={16} color="var(--rd-text-icon)" />
                  {item.label}
                </Link>
              ))}
              <button
                type="button"
                role="menuitem"
                className="rd-acct-item rd-acct-danger"
                onClick={() => {
                  setMenuOpen(false);
                  props.onSignOut();
                }}
              >
                <Icon icon={LogOut} size={16} color="var(--rd-red)" />
                {labels.signOut}
              </button>
            </div>
          </>
        ) : null}
        <button
          type="button"
          className="rd-side-account"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          title={labels.account}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span className="rd-side-avatar" aria-hidden="true">
            {initialsOf(props.accountName)}
          </span>
          <span className="rd-side-account-text">
            <b dir="auto">{props.accountName}</b>
            {props.accountRole ? <span>{props.accountRole}</span> : null}
          </span>
          <Icon icon={ChevronsUpDown} size={16} color="var(--rd-text-meta)" />
        </button>
      </div>
    </nav>
  );
}

/**
 * The bar above every page: "TRIOTY / Page" over the page's one-line
 * description, the page's own actions, then the bell.
 */
export function PageHeader({
  root,
  page,
  description,
  leading,
  actions,
}: {
  root: string;
  page: ReactNode;
  description?: ReactNode;
  /** Open-sidebar button when the sidebar is folded or on a phone. */
  leading?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="rd-header">
      {leading ?? null}
      <div className="rd-crumb">
        <div className="rd-crumb-path">
          <span className="rd-crumb-root">{root}</span>
          <span className="rd-crumb-sep">/</span>
          <h1 className="rd-crumb-page" style={{ margin: 0, fontSize: "inherit", lineHeight: "inherit" }}>
            {page}
          </h1>
        </div>
        {description ? <span className="rd-crumb-sub">{description}</span> : null}
      </div>
      <div className="rd-spacer" />
      <div className="rd-header-actions">{actions ?? null}</div>
    </header>
  );
}
